// Э-11. Справочники: таблицы с поиском, формы, импорт из Excel (ФТ-1.1).

import { useRef, useState } from "react";
import { navigate } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Drawer, Empty, Field, Input, Loading, Select, useAsync, useToast } from "../../shared/ui";
import { canReadCatalog } from "../access";
import { api, POUR, useCtx } from "../ctx";
import { fmtPhone } from "../../shared/format";

type FieldDef = { key: string; label: string; type?: "text" | "number" | "bool" | "time" | "select" | "ref"; options?: Record<string, string>; ref?: string; wide?: boolean };
type Def = { title: string; columns: string[]; fields: FieldDef[]; importable?: boolean; hint?: string };

const DEFS: Record<string, Def> = {
  customers: { title: "Клиенты", importable: true, columns: ["name", "inn", "phones", "contract"], fields: [
    { key: "name", label: "Название", wide: true }, { key: "kind", label: "Тип", type: "select", options: { legal: "Юрлицо", person: "Физлицо" } },
    { key: "inn", label: "ИНН" }, { key: "phones", label: "Телефоны" }, { key: "email", label: "Почта" }, { key: "contract", label: "Договор" },
    { key: "credit_limit", label: "Лимит, ₽", type: "number" }, { key: "priority", label: "Приоритет (крупные объекты по договору выше)", type: "number" },
    { key: "address", label: "Юридический адрес (для ТТН)", wide: true }, { key: "okpo", label: "ОКПО" },
    { key: "bank_account", label: "Расчётный счёт" }, { key: "bank_name", label: "Банк" }, { key: "bik", label: "БИК" }, { key: "corr_account", label: "Корр. счёт" },
    { key: "comment", label: "Комментарий", wide: true }] },
  sites: { title: "Объекты", importable: true, columns: ["name", "customer_name", "address", "intake_m3h"], hint: "Время в пути от каждого завода задаётся в карточке объекта", fields: [
    { key: "customer_id", label: "Клиент", type: "ref", ref: "customers", wide: true }, { key: "name", label: "Название", wide: true },
    { key: "address", label: "Адрес", wide: true }, { key: "zone_id", label: "Зона доставки", type: "ref", ref: "zones" },
    { key: "pour_method", label: "Подача", type: "select", options: POUR }, { key: "intake_m3h", label: "Интенсивность приёма, м³/ч", type: "number" },
    { key: "interval_minutes", label: "Интервал между машинами, мин", type: "number" }, { key: "work_from", label: "Работают с", type: "time" },
    { key: "work_to", label: "Работают до", type: "time" }, { key: "tower_cranes", label: "Башенных кранов", type: "number" },
    { key: "driver_comment", label: "Комментарий водителю", wide: true },
    { key: "lat", label: "Широта (для зоны объекта)", type: "number" }, { key: "lng", label: "Долгота", type: "number" }] },
  contacts: { title: "Контакты", columns: ["full_name", "position", "phone"], fields: [
    { key: "customer_id", label: "Клиент", type: "ref", ref: "customers", wide: true }, { key: "full_name", label: "ФИО" }, { key: "position", label: "Должность" },
    { key: "phone", label: "Телефон" }, { key: "messenger", label: "Мессенджер" }, { key: "notify", label: "Получает уведомления", type: "bool" }] },
  grades: { title: "Марки бетона", importable: true, columns: ["code", "class_b", "mark_m", "requires_lab_approval"], fields: [
    { key: "code", label: "Обозначение", wide: true }, { key: "class_b", label: "Класс" }, { key: "mark_m", label: "Марка" }, { key: "mobility", label: "Подвижность" },
    { key: "frost", label: "F" }, { key: "water", label: "W" }, { key: "filler", label: "Наполнитель" }, { key: "winter", label: "Зимний", type: "bool" },
    { key: "product_code", label: "Код продукции для ТТН (как в 1С)" }, { key: "full_name", label: "Наименование в ТТН", wide: true },
    { key: "requires_lab_approval", label: "Требует подтверждения лаборатории", type: "bool" }, { key: "active", label: "Активна", type: "bool" }] },
  vehicles: { title: "Транспорт", importable: true, columns: ["plate", "drum_m3", "owner", "status", "on_duty", "driver_name"],
    hint: "«Работает у нас сейчас» — машина в рабочем парке: из таких диспетчер выбирает на рейс. Наёмная уехала на другой завод — снимите галочку (или на странице «Машины»).", fields: [
    { key: "plate", label: "Госномер" }, { key: "kind", label: "Тип", type: "select", options: { mixer: "Миксер", pump: "Насос", dump: "Самосвал" } },
    { key: "drum_m3", label: "Объём барабана, м³", type: "number" }, { key: "brand", label: "Марка (для ТТН)" }, { key: "owner", label: "Владелец", type: "select", options: { own: "Свой", carrier: "Наёмный" } },
    { key: "carrier_id", label: "Перевозчик", type: "ref", ref: "carriers" }, { key: "home_plant_id", label: "База (завод)", type: "ref", ref: "plants" },
    { key: "status", label: "Статус", type: "select", options: { active: "В работе", repair: "Ремонт", day_off: "Выходной" } },
    { key: "on_duty", label: "Работает у нас сейчас (рабочий парк)", type: "bool" },
    { key: "driver_name", label: "Водитель (если не заведён в «Водителях»)" }, { key: "driver_phone", label: "Его телефон" }] },
  materials: { title: "Сырьё (материалы)", columns: ["name", "kind", "unit", "min_stock", "incoming_control", "active"],
    hint: "Норма в рецептуре задаётся в единице материала на 1 м³. Ниже минимального остатка — предупреждение на складе и у диспетчера.", fields: [
    { key: "name", label: "Название", wide: true },
    { key: "kind", label: "Вид", type: "select", options: { cement: "Цемент", sand: "Песок", gravel: "Щебень", water: "Вода", additive: "Добавка", other: "Прочее" } },
    { key: "unit", label: "Единица", type: "select", options: { "т": "т", "кг": "кг", "л": "л", "м³": "м³" } },
    { key: "min_stock", label: "Минимальный остаток", type: "number" },
    { key: "incoming_control", label: "Входной контроль лабораторией", type: "bool" },
    { key: "active", label: "Используется", type: "bool" }, { key: "comment", label: "Комментарий", wide: true }] },
  plants: { title: "Заводы", columns: ["name", "address", "timezone"], fields: [
    { key: "name", label: "Название" }, { key: "address", label: "Адрес", wide: true }, { key: "timezone", label: "Часовой пояс" },
    { key: "company_id", label: "Юрлицо", type: "ref", ref: "companies" },
    { key: "lat", label: "Широта (для зоны завода)", type: "number" }, { key: "lng", label: "Долгота", type: "number" }] },
  units: { title: "РБУ", columns: ["name", "plant_name", "load_minutes", "std_trip_m3", "active"], fields: [
    { key: "plant_id", label: "Завод", type: "ref", ref: "plants" }, { key: "name", label: "Название" },
    { key: "capacity_m3h", label: "Производительность, м³/ч", type: "number" }, { key: "load_minutes", label: "Погрузка миксера, мин", type: "number" },
    { key: "buffer_minutes", label: "Буфер между погрузками, мин", type: "number" }, { key: "std_trip_m3", label: "Стандартный рейс, м³", type: "number" },
    { key: "active", label: "Активна (не на ремонте)", type: "bool" }] },
  downtimes: { title: "Плановые простои РБУ", columns: ["unit_id", "start", "end", "reason"], fields: [
    { key: "unit_id", label: "РБУ", type: "ref", ref: "units" }, { key: "start", label: "Начало (ISO)" }, { key: "end", label: "Окончание (ISO)" }, { key: "reason", label: "Причина" }] },
  zones: { title: "Зоны доставки", columns: ["name", "default_travel_minutes", "delivery_price"], fields: [
    { key: "name", label: "Название" }, { key: "radius_km", label: "Радиус, км", type: "number" },
    { key: "default_travel_minutes", label: "Время в пути по умолчанию, мин", type: "number" }, { key: "delivery_price", label: "Цена доставки, ₽", type: "number" }] },
  drivers: { title: "Водители", columns: ["full_name", "phone", "vehicle_plate", "has_code", "active"],
    hint: "Водитель входит в приложение на телефоне по номеру телефона и коду. Код выдаётся в карточке водителя.", fields: [
    { key: "full_name", label: "ФИО", wide: true }, { key: "phone", label: "Телефон (он же вход в приложение)" },
    { key: "vehicle_id", label: "Машина по умолчанию", type: "ref", ref: "vehicles" }, { key: "carrier_id", label: "Перевозчик (для наёмных)", type: "ref", ref: "carriers" },
    { key: "license", label: "Водительское удостоверение № (для ТТН)" },
    { key: "active", label: "Работает (может входить)", type: "bool" }] },
  carriers: { title: "Перевозчики", columns: ["name", "inn", "contact"], hint: "Для наёмных машин — «Организация» в транспортном разделе ТТН.", fields: [
    { key: "name", label: "Название", wide: true }, { key: "inn", label: "ИНН" }, { key: "contact", label: "Контакт" }, { key: "phone", label: "Телефон" },
    { key: "address", label: "Юридический адрес (для ТТН)", wide: true }, { key: "okpo", label: "ОКПО" },
    { key: "bank_account", label: "Расчётный счёт" }, { key: "bank_name", label: "Банк" }, { key: "bik", label: "БИК" }, { key: "corr_account", label: "Корр. счёт" }] },
  "delay-reasons": { title: "Причины задержек", columns: ["name", "side"], fields: [
    { key: "name", label: "Причина" }, { key: "side", label: "Виновная сторона", type: "select", options: { plant: "Завод", client: "Клиент" } }, { key: "active", label: "Активна", type: "bool" }] },
  companies: { title: "Юрлица", columns: ["name", "inn", "address"], hint: "Грузоотправитель в ТТН: реквизиты и подписи «Отпуск разрешил» и «Главный бухгалтер» печатаются отсюда.", fields: [
    { key: "name", label: "Название", wide: true }, { key: "inn", label: "ИНН" }, { key: "kpp", label: "КПП" }, { key: "phone", label: "Телефон" },
    { key: "address", label: "Юридический адрес (для ТТН)", wide: true }, { key: "okpo", label: "ОКПО" },
    { key: "bank_account", label: "Расчётный счёт" }, { key: "bank_name", label: "Банк" }, { key: "bik", label: "БИК" }, { key: "corr_account", label: "Корр. счёт" },
    { key: "director_title", label: "Должность руководителя (Отпуск разрешил)" }, { key: "director_name", label: "ФИО руководителя" },
    { key: "accountant_name", label: "ФИО главного бухгалтера" }] },
};

const COL_LABEL: Record<string, string> = {
  name: "Название", inn: "ИНН", phones: "Телефоны", contract: "Договор", customer_name: "Клиент", address: "Адрес", intake_m3h: "м³/ч",
  full_name: "ФИО", position: "Должность", phone: "Телефон", code: "Марка", class_b: "Класс", mark_m: "М", requires_lab_approval: "Лаборатория",
  plate: "Госномер", drum_m3: "Барабан", owner: "Владелец", status: "Статус", driver_name: "Водитель", timezone: "Пояс", plant_name: "Завод",
  load_minutes: "Погрузка, мин", std_trip_m3: "Рейс, м³", active: "Активна", unit_id: "РБУ", start: "Начало", end: "Конец", reason: "Причина",
  default_travel_minutes: "В пути, мин", delivery_price: "Доставка, ₽", contact: "Контакт", side: "Сторона",
  vehicle_plate: "Машина", has_code: "Код выдан", on_duty: "У нас сейчас",
};

export function CatalogsPage({ name }: { name?: string }) {
  const { me } = useCtx();
  const visible = Object.keys(DEFS).filter((n) => canReadCatalog(me.role, n));
  const key = name && DEFS[name] && visible.includes(name) ? name : visible[0];
  return (
    <>
      <TopBar title="Справочники">
        <Select aria-label="Справочник" value={key} onChange={(e) => navigate(`/catalogs/${e.target.value}`)}
          options={visible.map((value) => ({ value, label: DEFS[value].title }))} style={{ width: 240 }} />
      </TopBar>
      <CatalogTable key={key} name={key} def={DEFS[key]} />
    </>
  );
}

function CatalogTable({ name, def }: { name: string; def: Def }) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const list = useAsync<any>(() => api.get(`/${name}`, { q: q || undefined, limit: 500 }), [name, q]);
  const [edit, setEdit] = useState<any | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const importFile = async (f: File) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.raw<any>("POST", `/${name}/import`, fd);
      toast.ok(`Создано: ${r.created}, обновлено: ${r.updated}${r.errors.length ? `, ошибок: ${r.errors.length}` : ""}`);
      list.reload();
    } catch (e) { toast.error(e); }
  };
  return (
    <div className="content">
      <div className="row">
        <Input placeholder="Поиск" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320 }} aria-label="Поиск" />
        <div className="grow" />
        {def.importable && <><Button onClick={() => file.current?.click()}>Импорт из Excel</Button>
          <input ref={file} type="file" accept=".xlsx" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} /></>}
        <Button variant="primary" onClick={() => setEdit({})}>Добавить</Button>
      </div>
      {def.hint && <p className="muted small">{def.hint}</p>}
      {!list.data ? <Loading error={list.error} onRetry={list.reload} /> : list.data.items.length === 0 ? (
        <Empty action={<Button onClick={() => setEdit({})}>Добавить первую запись</Button>}>Записей пока нет.</Empty>
      ) : (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr>{def.columns.map((c) => <th key={c}>{COL_LABEL[c] || c}</th>)}</tr></thead>
            <tbody>
              {list.data.items.map((r: any) => (
                <tr key={r.id} className="click" onClick={() => setEdit(r)}>
                  {def.columns.map((c) => <td key={c}>{cell(c, r[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <EditForm name={name} def={def} item={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); list.reload(); }} />}
    </div>
  );
}

function cell(key: string, v: any): string {
  if (v === true) return "да";
  if (v === false) return "";
  if (v === null || v === undefined) return "—";
  if (key === "owner") return v === "own" ? "свой" : "наёмный";
  if (key === "status") return ({ active: "в работе", repair: "ремонт", day_off: "выходной" } as any)[v] || v;
  if (key === "side") return v === "client" ? "клиент" : "завод";
  if (key === "phone") return fmtPhone(v);
  return String(v);
}

function EditForm({ name, def, item, onClose, onSaved }: { name: string; def: Def; item: any; onClose: () => void; onSaved: () => void }) {
  const { refs, reloadRefs } = useCtx();
  const toast = useToast();
  const isNew = !item.id;
  const [v, setV] = useState<Record<string, any>>(() => ({ active: true, on_duty: true, ...item }));
  const [travel, setTravel] = useState<Record<string, string>>(() => Object.fromEntries((item.travel || []).map((t: any) => [t.plant_id, String(t.minutes)])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const body: Record<string, any> = {};
    for (const f of def.fields) {
      let x = v[f.key];
      if (f.type === "number") x = x === "" || x === null || x === undefined ? null : Number(String(x).replace(",", "."));
      if (f.type === "bool") x = !!x;
      if ((f.type === "ref" || f.type === "time") && x === "") x = null;
      if (x !== undefined) body[f.key] = x;
    }
    if (name === "sites") body.travel = Object.entries(travel).filter(([, m]) => m).map(([plant_id, m]) => ({ plant_id, minutes: Number(m) }));
    try {
      if (isNew) await api.post(`/${name}`, body);
      else await api.patch(`/${name}/${item.id}`, body);
      toast.ok("Сохранено");
      if (["plants", "units", "grades", "delay-reasons"].includes(name)) reloadRefs();
      onSaved();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <Drawer narrow title={isNew ? `${def.title}: новая запись` : `${def.title}: изменение`} onClose={onClose} footer={<>
      <Button variant="primary" disabled={busy} onClick={save}>Сохранить</Button>
      <Button onClick={onClose}>Отмена</Button>
      {!isNew && <Button variant="danger" onClick={async () => {
        if (!confirm("Удалить запись? Она исчезнет из списков, история сохранится.")) return;
        try { await api.del(`/${name}/${item.id}`); onSaved(); } catch (e) { toast.error(e); }
      }}>Удалить</Button>}
    </>}>
      <div className="form-grid">
        {def.fields.map((f) => (
          <Field key={f.key} label={f.label} wide={f.wide}>
            {f.type === "bool" ? (
              <label className="check"><input type="checkbox" checked={!!v[f.key]} onChange={(e) => setV({ ...v, [f.key]: e.target.checked })} />да</label>
            ) : f.type === "select" ? (
              <Select value={v[f.key] ?? ""} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} options={Object.entries(f.options!).map(([value, label]) => ({ value, label }))} />
            ) : f.type === "ref" ? (
              <RefSelect ref_={f.ref!} value={v[f.key] ?? ""} onChange={(x) => setV({ ...v, [f.key]: x })} />
            ) : (
              <Input type={f.type === "time" ? "time" : "text"} inputMode={f.type === "number" ? "decimal" : undefined}
                value={v[f.key] === null || v[f.key] === undefined ? "" : String(v[f.key]).slice(0, f.type === "time" ? 5 : undefined)}
                onChange={(e) => setV({ ...v, [f.key]: e.target.value })} />
            )}
          </Field>
        ))}
        {name === "drivers" && !isNew && <DriverCode driver={item} />}
        {(name === "sites" || name === "plants") && (
          <p className="small muted wide">Координаты нужны для автоматических статусов рейса по телефону водителя (зона вокруг завода и объекта).
            Их можно взять в любой карте: правый клик по точке — «Что здесь?»; первое число — широта, второе — долгота.</p>
        )}
        {name === "sites" && refs.plants.map((p) => (
          <Field key={p.id} label={`В пути от: ${p.name}, мин`} hint="Пусто — по зоне доставки">
            <Input inputMode="numeric" value={travel[p.id] || ""} onChange={(e) => setTravel({ ...travel, [p.id]: e.target.value })} />
          </Field>
        ))}
      </div>
    </Drawer>
  );
}

/** Код входа водителя: показывается один раз, новый код отменяет старый. */
function DriverCode({ driver }: { driver: any }) {
  const { me } = useCtx();
  const toast = useToast();
  const [code, setCode] = useState<string | null>(null);
  if (!["dispatcher", "admin"].includes(me.role)) return null;
  return (
    <div className="wide stack tight">
      <b>Вход в приложение водителя</b>
      {code ? (
        <div className="alert ok">
          <div className="t">Код: <span className="num" style={{ fontSize: 22, letterSpacing: 3 }}>{code}</span></div>
          <div className="d">Продиктуйте водителю. Вход — номер телефона {fmtPhone(driver.phone)} и этот код. Больше код не покажется.</div>
        </div>
      ) : (
        <p className="small muted">{driver.has_code ? "Код уже выдан. Новый код отменит старый — водителя выйдет со всех телефонов." : "Код ещё не выдан — водитель не может войти."}</p>
      )}
      <div><Button onClick={async () => {
        try { const r = await api.post(`/drivers/${driver.id}/code`); setCode(r.code); } catch (e) { toast.error(e); }
      }}>{driver.has_code ? "Выдать новый код" : "Выдать код"}</Button></div>
    </div>
  );
}

function RefSelect({ ref_, value, onChange }: { ref_: string; value: string; onChange: (v: string) => void }) {
  const list = useAsync<any>(() => api.get(`/${ref_}`, { limit: 1000 }), [ref_]);
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} placeholder="—"
      options={(list.data?.items || []).map((x: any) => ({ value: x.id, label: x.name || x.full_name || x.code || x.plate || x.id }))} />
  );
}
